import { useEffect, useRef } from "react";
import { styled } from "styled-components";

import { BellIcon, SettingsIcon, UIButton } from "common/components";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { pushModalScreen } from "domains/modal/actions";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { fetchNotifications, markAllNotificationDone } from "domains/notifications/actions";
import { NotificationItem } from "domains/notifications/components/NotificationItem";
import {
  createNotificationsSelector,
  getNotificationHasNextPage,
  getNotificationIsPending,
  getNotificationList,
  getNotificationListOffset,
} from "domains/notifications/selectors";

const StyledEmptyList = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 3rem;
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledEmptyListIcon = styled(BellIcon)`
  width: 30px;
  height: 30px;
  margin-right: 1rem;
`;

const StyledText = styled.span`
  font-weight: bold;
`;

const StyledButton = styled(UIButton)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background: none;
  aspect-ratio: 1 / 1;
  padding: 5px;

  &:focus-visible,
  &:hover {
    > svg {
      color: ${({ theme }) => theme.colors.textBright};
    }
    background: ${({ theme }) => theme.colors.modalCardBackground};
  }
`;

const StyledSettingsIcon = styled(SettingsIcon)`
  color: ${({ theme }) => theme.colors.textFade};
  width: 24px;
  height: 24px;
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: ${({ theme }) => theme.colors.textBright};
  }
`;

export const NotificationsModal = () => {
  const dispatch = useDispatch();
  const items = useMemoisedSelector(getNotificationList, createNotificationsSelector);
  const skipMarking = useRef<boolean>(false);
  const hasNextPage = useSelector(getNotificationHasNextPage);
  const isPending = useSelector(getNotificationIsPending);
  const userNotificationOffset = useSelector(getNotificationListOffset);
  const translate = useTranslate();

  const loadMore = () => {
    dispatch(fetchNotifications({ offset: userNotificationOffset + 1 }));
  };

  useEffect(() => {
    dispatch(fetchNotifications({ offset: 0 }));
    return () => {
      // Prevent marking when switching to the detail notification modal
      if (!skipMarking.current) {
        dispatch(markAllNotificationDone());
      }
      skipMarking.current = false;
    };
  }, [dispatch]);

  const settingsOnClickHandle = () => {
    dispatch(pushModalScreen({ id: "NotificationsSettings" }));
  };

  const notificationOnClickHandle = (id: string) => () => {
    skipMarking.current = true;
    dispatch(pushModalScreen({ id: "NotificationDetail", params: { id } }));
  };

  const NotificationSettingsButton = () => (
    <StyledButton
      onClick={settingsOnClickHandle}
      aria-label={translate("app.settings.title")}
      title={translate("app.settings.title")}
    >
      <StyledSettingsIcon />
    </StyledButton>
  );

  return (
    <>
      <ModalHeader title={translate("app.notifications.title")} rightElement={<NotificationSettingsButton />} />
      <>
        {items.length === 0 ? (
          <StyledEmptyList>
            <StyledEmptyListIcon />
            <StyledText>{translate("app.notifications.empty_message")}</StyledText>
          </StyledEmptyList>
        ) : (
          <>
            {items.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} onClick={notificationOnClickHandle} />
            ))}
          </>
        )}
        {hasNextPage && (
          <UIButton onClick={loadMore} variant="outlined" fullwidth isLoading={isPending}>
            {translate("app.action.load_more")}
          </UIButton>
        )}
      </>
    </>
  );
};
